import React, { useState, useEffect, useRef } from "react";
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import ReactInputMask from "react-input-mask";

import { cn } from "@/lib";
import { MessageError } from "../common";

interface DatePickerProps
  extends Omit<React.ComponentProps<typeof ReactInputMask>, "mask"> {
  placeholder?: string;
  isInvalid?: boolean;
  isValid?: boolean;
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  messageError?: string;
}

interface ReactInputMaskRef extends ReactInputMask {
  input: HTMLInputElement;
}

interface CalendarProps {
  selectedDate: Date | null;
  onDateSelect: (date: Date) => void;
}

interface DateCellProps {
  day: number;
  isSelected: boolean;
  onSelect: () => void;
}
const Calendar: React.FC<CalendarProps> = ({ selectedDate, onDateSelect }) => {
  const userTimeZone =  'UTC';
  const [viewDate, setViewDate] = useState(selectedDate || new Date());

  useEffect(() => {
    if (selectedDate) {
      setViewDate(selectedDate);
    }
  }, [selectedDate]);

  const daysInMonth = new Date(
    viewDate.getFullYear(),
    viewDate.getMonth() + 1,
    0
  ).getDate();

  const firstDayOfMonth = new Date(
    viewDate.getFullYear(),
    viewDate.getMonth(),
    1
  );
  const startDay = firstDayOfMonth.getDay();
  const dates = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const weekDays = Array.from({ length: 7 }).map((_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - date.getDay() + i);
    return new Intl.DateTimeFormat(navigator.language, {
      weekday: "narrow",
      timeZone: userTimeZone,
    }).format(date);
  });

  const handlePrevMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  };

  const months = Array.from({ length: 12 }, (_, i) =>
    new Intl.DateTimeFormat(navigator.language, {
      month: "long",
      timeZone: userTimeZone,
    }).format(new Date(2024, i, 1))
  );

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setViewDate(new Date(viewDate.getFullYear(), parseInt(e.target.value), 1));
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setViewDate(new Date(parseInt(e.target.value), viewDate.getMonth(), 1));
  };

  return (
    <div className="absolute mt-2 bg-white border rounded-lg shadow-lg p-4 w-[280px] z-50">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <select
            value={viewDate.getMonth()}
            onChange={handleMonthChange}
            className="text-sm font-medium border-none bg-transparent focus:outline-none focus:ring-0 cursor-pointer pr-6"
          >
            {months.map((month, index) => (
              <option key={index} value={index}>
                {month}
              </option>
            ))}
          </select>
          <select
            value={viewDate.getFullYear()}
            onChange={handleYearChange}
            className="text-sm font-medium border-none bg-transparent focus:outline-none focus:ring-0 cursor-pointer"
          >
            {Array.from({ length: 50 }, (_, i) => 1980 + i).map(
              (year, index) => (
                <option key={index} value={year}>
                  {year}
                </option>
              )
            )}
          </select>
        </div>
        <div className="flex gap-1">
          <button
            onClick={handlePrevMonth}
            className="p-1 rounded-md hover:bg-gray-100 text-gray-600"
            type="button"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={handleNextMonth}
            className="p-1 rounded-md hover:bg-gray-100 text-gray-600"
            type="button"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekDays.map((day, index) => (
          <div
            key={index}
            className="text-xs text-gray-500 font-medium text-center"
          >
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: startDay }).map((_, index) => (
          <div key={`empty-${index}`} className="w-8 h-8" />
        ))}
        {dates.map((day, index) => {
          const currentDate = new Date(
            viewDate.getFullYear(),
            viewDate.getMonth(),
            day
          );
          return (
            <DateCell
              key={index}
              day={day}
              isSelected={
                !!selectedDate &&
                selectedDate.getDate() === day &&
                selectedDate.getMonth() === viewDate.getMonth() &&
                selectedDate.getFullYear() === viewDate.getFullYear()
              }
              onSelect={() => onDateSelect(currentDate)}
            />
          );
        })}
      </div>
    </div>
  );
};

const DateCell: React.FC<DateCellProps> = ({ day, isSelected, onSelect }) => {
  return (
    <button
      onClick={onSelect}
      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm transition-colors
        ${
          isSelected
            ? "bg-teal-500 text-white hover:bg-teal-600"
            : "text-zinc-700 hover:bg-zinc-100"
        }`}
    >
      {day}
    </button>
  );
};

export const DatePicker: React.FC<DatePickerProps> = ({
  placeholder,
  isInvalid,
  isValid,
  value,
  onChange,
  onFocus,
  onBlur,
  className,
  messageError,
  ...rest
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);

  const inputRef = useRef<ReactInputMaskRef>(null);

  useEffect(() => {
    if (value) {
      const parts = value.split("/");
      if (parts.length === 3) {
        const [day, month, year] = parts.map(Number);
        if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
          const newDate = new Date(year, month - 1, day);
          if (newDate.getTime()) {
            setSelectedDate(newDate);
          }
        }
      }
    } else {
      setSelectedDate(null);
    }
  }, [value]);

  const handleDateChange = (date: Date) => {
    setSelectedDate(date);
    if (onChange) {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const formattedDate = `${day}/${month}/${year}`;

      const event = {
        target: {
          name: inputRef.current?.props.name,
          value: formattedDate,
        },
      };
      onChange(event as any);
    }
    setIsFocused(false);
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as HTMLElement;
      const calendar = calendarRef.current;
      const input = inputRef.current?.input;
      const isCalendarIcon = target.closest(".calendar-icon");

      if (
        !calendar?.contains(target) &&
        !input?.contains(target) &&
        !isCalendarIcon
      ) {
        setIsFocused(false);
      }
    }

    if (isFocused) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isFocused]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    if (onChange) {
      onChange(e);
    }

    const parts = newValue.split("/");
    if (parts.length === 3) {
      const [day, month, year] = parts.map(Number);
      if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
        const newDate = new Date(year, month - 1, day);
        if (newDate.getTime()) {
          setSelectedDate(newDate);
        }
      }
    }
  };

  return (
    <div className="relative w-full">
      <div className="relative">
        <div className="space-y-1">
          <ReactInputMask
            ref={inputRef}
            mask="99/99/9999"
            value={value}
            placeholder={placeholder || "dd/mm/yyyy"}
            onChange={handleInputChange}
            onFocus={(e) => {
              setIsFocused(true);
              onFocus?.(e);
            }}
            onBlur={(e) => {
              onBlur?.(e);
              if (!value) setSelectedDate(null);
            }}
            className={cn(
              "flex h-10 w-full rounded-md border border-input bg-background px-4 py-2 text-sm placeholder:text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/35 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
              className,
              {
                "is-invalid": isInvalid,
              },
              {
                "is-valid": isValid,
              }
            )}
            {...rest}
          />
        </div>
        <CalendarIcon
          data-error={isInvalid ? true : false}
          data-isValid={isValid ? true : false}
          className="calendar-icon absolute data-[isValid=true]:right-10 data-[error=true]:right-10 right-3 top-1/2 transform -translate-y-1/2 text-gray-400 cursor-pointer size-4 z-[9999]"
          onClick={() => setIsFocused(true)}
        />
      </div>
      {isFocused && (
        <div ref={calendarRef}>
          <Calendar
            selectedDate={selectedDate}
            onDateSelect={handleDateChange}
          />
        </div>
      )}
      {isInvalid && messageError && (
        <MessageError isInvalid messageError={messageError} />
      )}
    </div>
  );
};
